import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, X, ChevronLeft, ChevronRight, Maximize2, Minimize2, ExternalLink, Search, PanelLeftClose, PanelLeftOpen, NotebookPen, AlertCircle, CornerDownRight, ArrowRightCircle } from 'lucide-react';
import { format, addDays, subDays, getISOWeek, startOfISOWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { api } from '../api/client';
import { useApp } from '../contexts/AppContext';
import { Checkbox, toast, StatusBadge } from './UI';
import TaskModal from './TaskModal';
import MarkdownEditor from './MarkdownEditor';

function getWeekId(date) {
  const y = format(startOfISOWeek(date), 'yyyy');
  const w = String(getISOWeek(date)).padStart(2, '0');
  return `${y}-${w}`;
}

// Sortierung des Tasks-Pools. Wird in localStorage gemerkt, damit die
// Auswahl einen Reload ueberlebt.
const POOL_SORTS = [
  { value: 'created', label: 'Zuletzt erstellt' },
  { value: 'due', label: 'Fälligkeit' },
  { value: 'title', label: 'Titel A–Z' },
];
const POOL_SORT_KEY = 'day-pool-sort';
const CARRYOVER_DISMISSED_KEY = (date) => `day-carryover-dismissed-${date}`;

// Datums-Spalte kommt als '2026-08-31T00:00:00.000Z' — nur den Datumsteil
// verwenden, sonst verschiebt die Zeitzone das Ergebnis um einen Tag.
const dueDayOf = (task) => task.next_step_due_date?.split('T')[0] || null;

function dueLabelOf(task) {
  const raw = dueDayOf(task);
  if (!raw) return null;
  const [y, m, d] = raw.split('-').map(Number);
  return format(new Date(y, m - 1, d), 'dd.MM.');
}

// Fingerabdruck des Tagesplans: nur die Felder, die tatsaechlich gespeichert
// werden. Der Server liefert Items mit zusaetzlichen Spalten zurueck — ohne
// diese Reduktion wuerde der Autosave nach jedem Laden sofort wieder feuern.
const planFingerprint = (retro, mood, items) => JSON.stringify({
  retro: retro || '',
  mood: mood || '',
  items: items.map(it => [it.task_id || null, it.adhoc_title || null, it.section, !!it.is_checked]),
});

const MOODS = [
  { key: 'great', label: '😄 Sehr gut' },
  { key: 'good', label: '🙂 Gut' },
  { key: 'okay', label: '😐 Ok' },
  { key: 'rough', label: '😔 Schwierig' },
];

// Section is defined OUTSIDE the main component to keep its identity stable across renders.
// (Inline-Definition would re-create the component every render → Inputs lose focus per keystroke.)
function Section({
  label, emoji, items, section, adhocKey,
  focusMode, adhocInput, setAdhocInput,
  showTaskPicker, setShowTaskPicker,
  availableTasks, toggleItem, removeItem, addAdhoc, addTask,
  setOpenTaskId, onDrop, isDropActive, convertToTask, convertingIndex,
}) {
  const [pickerSearch, setPickerSearch] = useState('');
  const filteredPicker = pickerSearch.trim()
    ? availableTasks.filter(t => t.title.toLowerCase().includes(pickerSearch.toLowerCase()))
    : availableTasks;

  return (
    <div
      className="card"
      style={{
        flex: section === 'top' ? '0 0 auto' : 1,
        outline: isDropActive ? '2px solid var(--accent)' : 'none',
        outlineOffset: -2,
        transition: 'outline 0.1s',
      }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
      onDrop={(e) => { e.preventDefault(); onDrop && onDrop(section); }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, flex: 1 }}>{emoji} {label}</span>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{items.filter(i => i.is_checked).length}/{items.length}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {items.length === 0 && <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Leer — hier Tasks per Drag fallen lassen oder unten hinzufügen</div>}
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
            background: 'var(--bg-2)', borderRadius: 'var(--radius)',
            opacity: item.is_checked ? 0.5 : 1, transition: 'opacity 0.2s' }}>
            <Checkbox checked={item.is_checked} onChange={() => toggleItem(section, i)} />
            <span
              onClick={() => item.task_id && setOpenTaskId(item.task_id)}
              style={{
                flex: 1, fontSize: 13,
                textDecoration: item.is_checked ? 'line-through' : 'none',
                color: item.is_checked ? 'var(--text-3)' : 'var(--text)',
                cursor: item.task_id ? 'pointer' : 'default',
              }}
              title={item.task_id ? 'Task-Detail öffnen' : ''}
            >
              {item.task_title || item.adhoc_title}
              {item.task_id && <ExternalLink size={10} style={{ verticalAlign: 'middle', marginLeft: 6, color: 'var(--text-3)' }} />}
            </span>
            {!item.task_id && item.adhoc_title && (
              <button className="btn btn-icon btn-ghost btn-sm"
                disabled={convertingIndex === `${section}-${i}`}
                onClick={() => convertToTask(section, i)}
                title="In echten Task umwandeln — dann greifen Fälligkeit, Status und die Inaktiv-Regel">
                <ArrowRightCircle size={12} />
              </button>
            )}
            <button className="btn btn-icon btn-ghost btn-sm" onClick={() => removeItem(section, i)}><X size={12} /></button>
          </div>
        ))}
      </div>

      {!focusMode && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowTaskPicker(showTaskPicker === section ? null : section)}><Plus size={12} /> Aus Tasks</button>
          <input value={adhocInput[adhocKey]} onChange={e => setAdhocInput(a => ({ ...a, [adhocKey]: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && addAdhoc(section)}
            placeholder="Schnell hinzufügen..." style={{ flex: 1 }} />
          <button className="btn btn-ghost btn-sm" onClick={() => addAdhoc(section)}><Plus size={12} /></button>
        </div>
      )}

      {showTaskPicker === section && (
        <div style={{ marginTop: 10, background: 'var(--bg-3)', borderRadius: 'var(--radius)', padding: 8 }}>
          <div style={{ position: 'relative', marginBottom: 6 }}>
            <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
            <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)}
              placeholder="Tasks durchsuchen..." style={{ paddingLeft: 26, fontSize: 12 }} autoFocus />
          </div>
          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
            {filteredPicker.length === 0 && (
              <div style={{ color: 'var(--text-3)', fontSize: 12, padding: 8, textAlign: 'center' }}>Keine passenden Tasks</div>
            )}
            {filteredPicker.map(task => (
              <div key={task.id} onClick={() => { addTask(task, section); setPickerSearch(''); }}
                style={{ padding: '6px 10px', cursor: 'pointer', borderRadius: 4, fontSize: 13, display: 'flex', gap: 8 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <Plus size={12} color="var(--text-3)" />
                <span style={{ flex: 1 }}>{task.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DayPlanning({ initialDate }) {
  const { tasks, refreshTasks, isDone, statusValues } = useApp();
  const [currentDate, setCurrentDate] = useState(initialDate ? new Date(initialDate) : new Date());
  const [topPrios, setTopPrios] = useState([]);
  const [operational, setOperational] = useState([]);
  const [retro, setRetro] = useState('');
  const [notes, setNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(null); // null | 'saving' | 'saved'
  const [mood, setMood] = useState('');
  const [weekPlan, setWeekPlan] = useState(null);
  const [showWeekPlan, setShowWeekPlan] = useState(true);
  const [focusMode, setFocusMode] = useState(false);
  const [showTaskPicker, setShowTaskPicker] = useState(null);
  const [adhocInput, setAdhocInput] = useState({ top: '', op: '' });
  const [saving, setSaving] = useState(false);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [panelSearch, setPanelSearch] = useState('');
  const [showTodayDue, setShowTodayDue] = useState(true);
  const [dayMarks, setDayMarks] = useState(new Set());
  const [planSaved, setPlanSaved] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [carryover, setCarryover] = useState(null); // { date, items } | null
  const [carryoverDismissed, setCarryoverDismissed] = useState(false);
  const [convertingIndex, setConvertingIndex] = useState(null);
  const [poolSort, setPoolSort] = useState(() => {
    try { return localStorage.getItem(POOL_SORT_KEY) || 'created'; } catch { return 'created'; }
  });

  useEffect(() => {
    try { localStorage.setItem(POOL_SORT_KEY, poolSort); } catch { /* Private Mode o.ae. */ }
  }, [poolSort]);

  const dateStr = format(currentDate, 'yyyy-MM-dd');
  const weekId = getWeekId(currentDate);
  const weekMonday = format(startOfISOWeek(currentDate), 'yyyy-MM-dd');

  // Load per-day marks (strikethrough for "Heute fällig" tasks)
  useEffect(() => {
    let cancelled = false;
    api.getMarks({ period_type: 'day', period_date: dateStr }).then(rows => {
      if (cancelled) return;
      setDayMarks(new Set((rows || []).map(r => r.task_id)));
    }).catch(() => { if (!cancelled) setDayMarks(new Set()); });
    return () => { cancelled = true; };
  }, [dateStr]);

  const toggleDayMark = async (taskId) => {
    const isMarked = dayMarks.has(taskId);
    setDayMarks(prev => {
      const next = new Set(prev);
      if (isMarked) next.delete(taskId); else next.add(taskId);
      return next;
    });
    try {
      if (isMarked) await api.unsetMark({ task_id: taskId, period_type: 'day', period_date: dateStr });
      else await api.setMark({ task_id: taskId, period_type: 'day', period_date: dateStr });
    } catch (err) {
      // Revert on error
      setDayMarks(prev => {
        const next = new Set(prev);
        if (isMarked) next.add(taskId); else next.delete(taskId);
        return next;
      });
      toast(err.message || 'Mark konnte nicht gespeichert werden', 'error');
    }
  };

  const weekGoals = useMemo(
    () => tasks.filter(t => t.scope === 'week' && t.period_date?.split('T')[0] === weekMonday),
    [tasks, weekMonday]
  );

  const taskById = useMemo(() => {
    const m = new Map();
    for (const t of tasks) m.set(t.id, t);
    return m;
  }, [tasks]);
  const editTask = openTaskId ? taskById.get(openTaskId) : null;

  // Der zuletzt gespeicherte bzw. geladene Stand. Der Autosave vergleicht
  // dagegen und laeuft erst los, wenn wirklich etwas anders ist.
  const snapshotRef = useRef(null); // { date, json }
  const pendingRef = useRef(null);  // { date, body, json }
  const autosaveTimer = useRef(null);
  const dateRef = useRef(dateStr);
  useEffect(() => { dateRef.current = dateStr; }, [dateStr]);

  const load = useCallback(async () => {
    try {
      const [day, week] = await Promise.all([api.getDayPlan(dateStr), api.getWeekPlan(weekId)]);
      setWeekPlan(week);
      const top = day?.items?.filter(i => i.section === 'top') || [];
      const op = day?.items?.filter(i => i.section === 'operational') || [];
      setTopPrios(top);
      setOperational(op);
      setRetro(day?.retro || '');
      setNotes(day?.notes || '');
      setMood(day?.mood || '');
      setPlanSaved(null);
      snapshotRef.current = { date: dateStr, json: planFingerprint(day?.retro, day?.mood, [...top, ...op]) };
    } catch {}
  }, [dateStr, weekId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (initialDate) setCurrentDate(new Date(initialDate)); }, [initialDate]);

  // Offene Ad-hoc-Punkte des letzten Tages davor anbieten. Bewusst als Angebot
  // und nicht automatisch: waere der Uebertrag gratis und unsichtbar, wuechse
  // die Liste um Punkte, die man nie bewusst abgelehnt hat.
  useEffect(() => {
    let cancelled = false;
    setCarryover(null);
    let dismissed = false;
    try { dismissed = localStorage.getItem(CARRYOVER_DISMISSED_KEY(dateStr)) === '1'; } catch { /* Private Mode o.ae. */ }
    setCarryoverDismissed(dismissed);
    if (dismissed) return undefined;
    api.getDayCarryover(dateStr)
      .then(res => { if (!cancelled) setCarryover(res); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dateStr]);

  // Schon uebernommene Punkte nicht erneut anbieten — der Titel ist der
  // einzige Anker, den eine Ad-hoc-Zeile hat.
  const carryoverItems = useMemo(() => {
    if (carryoverDismissed || !carryover?.items?.length) return [];
    const present = new Set(
      [...topPrios, ...operational]
        .filter(it => !it.task_id && it.adhoc_title)
        .map(it => it.adhoc_title)
    );
    return carryover.items.filter(it => !present.has(it.adhoc_title));
  }, [carryover, carryoverDismissed, topPrios, operational]);

  const carryoverLabel = useMemo(() => {
    const raw = carryover?.date?.split('T')[0];
    if (!raw) return '';
    const [y, m, d] = raw.split('-').map(Number);
    return format(new Date(y, m - 1, d), 'EEEE, dd.MM.', { locale: de });
  }, [carryover]);

  const acceptCarryover = () => {
    const mk = (it, section) => ({ task_id: null, adhoc_title: it.adhoc_title, is_checked: false, section });
    const top = carryoverItems.filter(it => it.section === 'top');
    const op = carryoverItems.filter(it => it.section !== 'top');
    if (top.length) setTopPrios(its => [...its, ...top.map(it => mk(it, 'top'))]);
    if (op.length) setOperational(its => [...its, ...op.map(it => mk(it, 'operational'))]);
    setCarryover(null); // Autosave schreibt den Rest
  };

  const dismissCarryover = () => {
    setCarryoverDismissed(true);
    try { localStorage.setItem(CARRYOVER_DISMISSED_KEY(dateStr), '1'); } catch { /* Private Mode o.ae. */ }
  };

  // Wandert ein Kleinkram-Punkt staendig mit, ist er kein Kleinkram mehr.
  // Als echter Task greifen Faelligkeit, Status und die Inaktiv-Regel.
  const convertToTask = async (section, i) => {
    const list = section === 'top' ? topPrios : operational;
    const item = list[i];
    if (!item || item.task_id || !item.adhoc_title) return;
    setConvertingIndex(`${section}-${i}`);
    try {
      const task = await api.createTask({
        title: item.adhoc_title,
        status: statusValues[0],
        scope: 'task',
        next_step_due_date: dateStr,
      });
      const setter = section === 'top' ? setTopPrios : setOperational;
      setter(its => its.map((it, j) => j === i
        ? { ...it, task_id: task.id, task_title: task.title, adhoc_title: null }
        : it));
      refreshTasks();
      toast('Task angelegt — fällig heute', 'success');
    } catch (err) {
      toast(err.message || 'Task konnte nicht angelegt werden', 'error');
    } finally { setConvertingIndex(null); }
  };

  const buildPlanPayload = useCallback(() => {
    const items = [
      ...topPrios.map(it => ({ ...it, section: 'top' })),
      ...operational.map(it => ({ ...it, section: 'operational' })),
    ];
    // notes bewusst nicht mitschicken — der Notizblock speichert selbst und
    // wuerde sonst mit einem aelteren Stand ueberschrieben.
    return {
      body: { retro, mood, items, week_plan_id: weekPlan?.id },
      json: planFingerprint(retro, mood, items),
    };
  }, [topPrios, operational, retro, mood, weekPlan]);

  const flushPlan = useCallback(async () => {
    clearTimeout(autosaveTimer.current);
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    try {
      await api.saveDayPlan(pending.date, pending.body);
      snapshotRef.current = { date: pending.date, json: pending.json };
      if (dateRef.current === pending.date) setPlanSaved('saved');
    } catch (err) {
      if (dateRef.current === pending.date) setPlanSaved('error');
      toast(err.message || 'Tagesplan konnte nicht gespeichert werden', 'error');
    }
  }, []);

  // Autosave fuer Top Prios, Operatives & Kleinkram, Stimmung und Rueckblick.
  // Abgehakte Punkte und schnell eingeworfene Ad-hoc-Zeilen sind sonst weg,
  // sobald man ohne "Tag speichern" weiterklickt.
  useEffect(() => {
    const snap = snapshotRef.current;
    if (!snap || snap.date !== dateStr) return; // Tag noch nicht geladen
    const { body, json } = buildPlanPayload();
    if (json === snap.json) return;
    pendingRef.current = { date: dateStr, body, json };
    setPlanSaved('saving');
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(flushPlan, 1000);
  }, [dateStr, buildPlanPayload, flushPlan]);

  // Beim Tageswechsel und beim Verlassen der Ansicht sofort schreiben, statt
  // die angefangene Debounce-Sekunde verfallen zu lassen.
  useEffect(() => () => { flushPlan(); }, [dateStr, flushPlan]);

  const save = async () => {
    clearTimeout(autosaveTimer.current);
    pendingRef.current = null;
    setSaving(true);
    const { body, json } = buildPlanPayload();
    try {
      await api.saveDayPlan(dateStr, body);
      snapshotRef.current = { date: dateStr, json };
      setPlanSaved('saved');
      toast('Tagesplan gespeichert', 'success');
    } catch (err) { setPlanSaved('error'); toast(err.message, 'error'); }
    finally { setSaving(false); }
  };

  // Der Notizblock speichert selbstaendig — ein Scratchpad, bei dem man erst
  // beim Weiterklicken merkt dass man auf "Tag speichern" vergessen hat, ist wertlos.
  // Datum wird beim Planen festgehalten, damit ein Tageswechsel nichts verschiebt.
  const notesTimer = useRef(null);
  const pendingNotesRef = useRef(null); // { date, value }

  const flushNotes = useCallback(async () => {
    clearTimeout(notesTimer.current);
    const pending = pendingNotesRef.current;
    if (!pending) return;
    pendingNotesRef.current = null;
    try {
      await api.saveDayPlan(pending.date, { notes: pending.value });
      if (dateRef.current === pending.date) setNotesSaved('saved');
    } catch (err) {
      if (dateRef.current === pending.date) setNotesSaved(null);
      toast(err.message || 'Notizen konnten nicht gespeichert werden', 'error');
    }
  }, []);

  // Wie beim Tagesplan: angefangene Debounce-Sekunde beim Tageswechsel und
  // beim Verlassen der Ansicht ausschreiben statt verfallen lassen.
  useEffect(() => () => { flushNotes(); }, [dateStr, flushNotes]);

  const onNotesChange = (value) => {
    setNotes(value);
    setNotesSaved('saving');
    pendingNotesRef.current = { date: dateStr, value };
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(flushNotes, 1000);
  };

  const toggleItem = (section, i) => {
    const setter = section === 'top' ? setTopPrios : setOperational;
    setter(its => its.map((it, j) => j === i ? { ...it, is_checked: !it.is_checked } : it));
  };

  const removeItem = (section, i) => {
    const setter = section === 'top' ? setTopPrios : setOperational;
    setter(its => its.filter((_, j) => j !== i));
  };

  const addTask = (task, section) => {
    const item = { task_id: task.id, task_title: task.title, adhoc_title: null, is_checked: false, section };
    if (section === 'top') setTopPrios(its => [...its, item]);
    else setOperational(its => [...its, item]);
    setShowTaskPicker(null);
  };

  const addAdhoc = (section) => {
    const key = section === 'top' ? 'top' : 'op';
    if (!adhocInput[key].trim()) return;
    const item = { task_id: null, adhoc_title: adhocInput[key].trim(), is_checked: false, section };
    if (section === 'top') setTopPrios(its => [...its, item]);
    else setOperational(its => [...its, item]);
    setAdhocInput(a => ({ ...a, [key]: '' }));
  };

  const allItems = [...topPrios, ...operational];
  const checkedCount = allItems.filter(i => i.is_checked).length;
  const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');

  const planTaskIds = useMemo(() => {
    const s = new Set();
    for (const it of topPrios) if (it.task_id) s.add(it.task_id);
    for (const it of operational) if (it.task_id) s.add(it.task_id);
    return s;
  }, [topPrios, operational]);
  const isInDayPlan = (taskId) => planTaskIds.has(taskId);

  const availableTasks = useMemo(
    () => tasks.filter(t => !isDone(t) && !planTaskIds.has(t.id)),
    [tasks, planTaskIds, isDone]
  );

  // Faellig heisst: bis einschliesslich zum angezeigten Tag. Ueberfaelliges
  // gehoert genau hierher — sonst rutscht es aus dem Blick, sobald sein Tag
  // vorbei ist. Aeltestes zuerst.
  const todayDueTasks = useMemo(
    () => tasks
      .filter(t => {
        const due = dueDayOf(t);
        return due && due <= dateStr && !isDone(t) && !planTaskIds.has(t.id);
      })
      .sort((a, b) => dueDayOf(a).localeCompare(dueDayOf(b))),
    [tasks, dateStr, planTaskIds, isDone]
  );

  const overdueCount = useMemo(
    () => todayDueTasks.filter(t => dueDayOf(t) < dateStr).length,
    [todayDueTasks, dateStr]
  );

  const poolTasks = useMemo(() => {
    const q = panelSearch.trim().toLowerCase();
    const list = availableTasks
      .filter(t => !q || t.title.toLowerCase().includes(q) || (t.next_step || '').toLowerCase().includes(q));

    const byCreated = (a, b) => new Date(b.created_at) - new Date(a.created_at);
    const comparators = {
      // Tasks ohne Datum ans Ende — sonst verdecken sie das Dringende oben.
      due: (a, b) => {
        const da = dueDayOf(a), db = dueDayOf(b);
        if (!da && !db) return byCreated(a, b);
        if (!da) return 1;
        if (!db) return -1;
        return da.localeCompare(db) || byCreated(a, b);
      },
      title: (a, b) => a.title.localeCompare(b.title, 'de'),
      created: byCreated,
    };
    return list.sort(comparators[poolSort] || byCreated);
  }, [availableTasks, panelSearch, poolSort]);

  const onTaskDragStart = (taskId) => (e) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', taskId); } catch {}
  };
  const onTaskDragEnd = () => setDraggedTaskId(null);

  const handleSectionDrop = (section) => {
    if (!draggedTaskId) return;
    const t = taskById.get(draggedTaskId);
    setDraggedTaskId(null);
    if (!t) return;
    if (isInDayPlan(t.id)) return;
    addTask(t, section);
  };

  const DraggableTaskCard = ({ task, compact, onClick, marked, onToggleMark, showDue }) => {
    const due = dueDayOf(task);
    const overdue = !!due && due < dateStr;
    return (
      <div
        draggable
        onDragStart={onTaskDragStart(task.id)}
        onDragEnd={onTaskDragEnd}
        onClick={onClick}
        style={{
          background: 'var(--bg-3)', border: `1px solid ${overdue && !marked ? 'var(--red)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)', padding: compact ? '5px 8px' : '6px 8px',
          marginBottom: 4, cursor: 'grab', color: 'var(--text)', fontSize: 12,
          opacity: draggedTaskId === task.id ? 0.4 : (marked ? 0.5 : 1),
          userSelect: 'none',
        }}
        title={task.next_step || task.title}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {onToggleMark && (
            <span onClick={(e) => { e.stopPropagation(); onToggleMark(task.id); }}>
              <Checkbox checked={!!marked} onChange={() => {}} />
            </span>
          )}
          <span style={{
            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            textDecoration: marked ? 'line-through' : 'none',
            color: marked ? 'var(--text-3)' : 'var(--text)',
          }}>{task.title}</span>
          {showDue && due && (
            <span style={{
              fontSize: 10, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 3,
              color: overdue && !marked ? 'var(--red)' : 'var(--text-3)',
            }}>
              {overdue && <AlertCircle size={10} />}{dueLabelOf(task)}
            </span>
          )}
          {!compact && <StatusBadge status={task.status} />}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Tasks-Pool */}
      {panelOpen && !focusMode && (
        <div style={{
          width: 280, borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', background: 'var(--bg-1)',
        }}>
          <div style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>Tasks-Pool</span>
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{poolTasks.length}</span>
            </div>
            <div style={{ position: 'relative' }}>
              <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
              <input value={panelSearch} onChange={e => setPanelSearch(e.target.value)}
                placeholder="Filtern..." style={{ paddingLeft: 26, fontSize: 12 }} />
            </div>
            <select value={poolSort} onChange={e => setPoolSort(e.target.value)}
              title="Sortierung" style={{ fontSize: 12, marginTop: 6 }}>
              {POOL_SORTS.map(o => <option key={o.value} value={o.value}>Sortierung: {o.label}</option>)}
            </select>
            <p style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 6 }}>
              Tasks hier in Top Prio oder Operatives ziehen. Klick öffnet das Detail.
            </p>
          </div>
          <div style={{ overflow: 'auto', padding: 12 }}>
            {poolTasks.map(t => (
              <DraggableTaskCard key={t.id} task={t} showDue onClick={() => setOpenTaskId(t.id)} />
            ))}
            {poolTasks.length === 0 && (
              <div style={{ color: 'var(--text-3)', fontSize: 12, textAlign: 'center', padding: 20 }}>
                Keine offenen Tasks
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ padding: focusMode ? '40px' : '24px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: focusMode ? '100%' : 800, margin: '0 auto', width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!focusMode && (
              <button className="btn btn-ghost btn-sm" onClick={() => setPanelOpen(o => !o)} title={panelOpen ? 'Pool ausblenden' : 'Pool einblenden'}>
                {panelOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
              </button>
            )}
            <div style={{ flex: 1 }}>
              <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 28 }}>
                {isToday ? 'Heute' : format(currentDate, 'EEEE', { locale: de })}
              </h1>
              <div style={{ color: 'var(--text-3)', fontSize: 13 }}>
                {format(currentDate, 'dd. MMMM yyyy', { locale: de })} · KW {getISOWeek(currentDate)} · {checkedCount}/{allItems.length} erledigt
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setCurrentDate(d => subDays(d, 1))}><ChevronLeft size={14} /></button>
            <button className="btn btn-ghost btn-sm" onClick={() => setCurrentDate(new Date())}>Heute</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setCurrentDate(d => addDays(d, 1))}><ChevronRight size={14} /></button>
            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setFocusMode(f => !f)} title="Fokus-Modus">
              {focusMode ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>

          {/* Wochenziele (aus Planung) */}
          {weekGoals.length > 0 && !focusMode && (
            <div style={{ background: 'var(--accent-dim)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: showWeekPlan ? 10 : 0 }}>
                <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 500 }}>⭐ Wochenziele (KW {getISOWeek(currentDate)})</span>
                <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto', padding: '2px 6px' }} onClick={() => setShowWeekPlan(v => !v)}>
                  {showWeekPlan ? 'Einklappen' : 'Ausklappen'}
                </button>
              </div>
              {showWeekPlan && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {weekGoals.map(g => (
                    <div key={g.id}
                      onClick={() => setOpenTaskId(g.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 6px', cursor: 'pointer', borderRadius: 4 }}
                      title="Task-Detail öffnen"
                    >
                      <span style={{ flex: 1, color: isDone(g) ? 'var(--text-3)' : 'var(--text)', textDecoration: isDone(g) ? 'line-through' : 'none' }}>
                        {g.title}
                      </span>
                      <StatusBadge status={g.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Fällig & überfällig */}
          {todayDueTasks.length > 0 && !focusMode && (
            <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: showTodayDue ? 10 : 0 }}>
                <span style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 500 }}>
                  📌 {isToday ? 'Heute fällig' : 'Fällig'} ({todayDueTasks.length})
                </span>
                {overdueCount > 0 && (
                  <span style={{ fontSize: 11, color: 'var(--red)', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <AlertCircle size={11} /> davon {overdueCount} überfällig
                  </span>
                )}
                <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto', padding: '2px 6px' }} onClick={() => setShowTodayDue(v => !v)}>
                  {showTodayDue ? 'Einklappen' : 'Ausklappen'}
                </button>
              </div>
              {showTodayDue && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {todayDueTasks.map(t => (
                    <DraggableTaskCard key={t.id} task={t} showDue onClick={() => setOpenTaskId(t.id)}
                      marked={dayMarks.has(t.id)} onToggleMark={toggleDayMark} />
                  ))}
                  <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>
                    Tipp: per Häkchen für heute abhaken (Status bleibt unverändert) oder in Top Prio / Operatives ziehen.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Offene Punkte vom letzten Tag */}
          {carryoverItems.length > 0 && !focusMode && (
            <div style={{ background: 'var(--bg-1)', border: '1px dashed var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <CornerDownRight size={13} color="var(--text-3)" />
                <span style={{ fontSize: 12, fontWeight: 500 }}>
                  {carryoverItems.length} offene{carryoverItems.length === 1 ? 'r Punkt' : ' Punkte'} vom {carryoverLabel}
                </span>
                <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={acceptCarryover}>Übernehmen</button>
                <button className="btn btn-ghost btn-sm" onClick={dismissCarryover}>Ignorieren</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {carryoverItems.map((it, i) => (
                  <span key={i} style={{ fontSize: 11, color: 'var(--text-2)', background: 'var(--bg-3)', borderRadius: 4, padding: '3px 7px' }}>
                    {it.adhoc_title}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Top Prios */}
          <Section
            label="Top Prioritäten" emoji="🔥" items={topPrios} section="top" adhocKey="top"
            focusMode={focusMode} adhocInput={adhocInput} setAdhocInput={setAdhocInput}
            showTaskPicker={showTaskPicker} setShowTaskPicker={setShowTaskPicker}
            availableTasks={availableTasks} toggleItem={toggleItem} removeItem={removeItem}
            addAdhoc={addAdhoc} addTask={addTask} setOpenTaskId={setOpenTaskId}
            onDrop={handleSectionDrop} isDropActive={!!draggedTaskId}
            convertToTask={convertToTask} convertingIndex={convertingIndex}
          />

          {/* Operational */}
          {!focusMode && (
            <Section
              label="Operatives & Kleinkram" emoji="⚙️" items={operational} section="operational" adhocKey="op"
              focusMode={focusMode} adhocInput={adhocInput} setAdhocInput={setAdhocInput}
              showTaskPicker={showTaskPicker} setShowTaskPicker={setShowTaskPicker}
              availableTasks={availableTasks} toggleItem={toggleItem} removeItem={removeItem}
              addAdhoc={addAdhoc} addTask={addTask} setOpenTaskId={setOpenTaskId}
              onDrop={handleSectionDrop} isDropActive={!!draggedTaskId}
              convertToTask={convertToTask} convertingIndex={convertingIndex}
            />
          )}

          {/* Notizblock — Scratchpad fuer Operatives & Kleinkram */}
          {!focusMode && (
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <NotebookPen size={15} /> Notizblock
                </label>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  Operatives &amp; Kleinkram — Zwischenstaende, Telefonnotizen, Screenshots
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)' }}>
                  {notesSaved === 'saving' ? 'Speichert …' : notesSaved === 'saved' ? 'Gespeichert' : ''}
                </span>
              </div>
              <MarkdownEditor
                value={notes}
                onChange={onNotesChange}
                minHeight={160}
                mic
                storageKey="md-mode-day-notes"
                placeholder="Schnelle Notizen für heute. Markdown, Checklisten (- [ ] …) und Screenshots per Strg+V."
              />
            </div>
          )}

          {/* Mood + Retro */}
          {!focusMode && (
            <div className="card">
              <label style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)', marginBottom: 10 }}>
                📝 Tagesrückblick
              </label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {MOODS.map(m => (
                  <button key={m.key} className="btn btn-ghost btn-sm"
                    style={{ borderColor: mood === m.key ? 'var(--accent)' : 'var(--border)', color: mood === m.key ? 'var(--accent)' : 'var(--text-2)' }}
                    onClick={() => setMood(m.key === mood ? '' : m.key)}>
                    {m.label}
                  </button>
                ))}
              </div>
              <textarea value={retro} onChange={e => setRetro(e.target.value)}
                rows={3} placeholder="Wie war der Tag? Was lief gut, was nicht? Was nehme ich mit?" />
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: planSaved === 'error' ? 'var(--red)' : 'var(--text-3)' }}>
              {planSaved === 'saving' ? 'Speichert automatisch …'
                : planSaved === 'saved' ? 'Automatisch gespeichert'
                : planSaved === 'error' ? 'Nicht gespeichert — bitte erneut versuchen'
                : ''}
            </span>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Speichern...' : 'Tag speichern'}
            </button>
          </div>
        </div>
      </div>

      {editTask && (
        <TaskModal
          task={editTask}
          onClose={() => setOpenTaskId(null)}
          onSaved={refreshTasks}
          onNavigate={(t) => setOpenTaskId(t?.id || null)}
        />
      )}
    </div>
  );
}
